import React, { useState } from "react";
import moment from "moment";

const to12h = (time24) => {
  if (!time24 || !time24.includes(":")) return "—";
  const [hStr, mStr] = time24.split(":");
  const h = parseInt(hStr, 10);
  const suffix = h < 12 ? "AM" : "PM";
  const h12 = h % 12 || 12;
  return `${h12}:${mStr} ${suffix}`;
};

const getShiftKey = (startTime) => {
  if (!startTime) return "pm";
  const h = parseInt(startTime.split(":")[0], 10);
  return h < 12 ? "am" : "pm";
};

const getStatusBadge = (att) => {
  if (!att) return { label: "Not Checked In", cls: "sdt-badge--muted" };
  const sv = att.status_value;
  if (sv === "on_leave") return { label: "On Leave",     cls: "sdt-badge--grey"   };
  if (sv === "day_off")  return { label: "Day Off",      cls: "sdt-badge--light"  };
  if (sv === "absent")   return { label: "Absent",       cls: "sdt-badge--red"    };
  // att.status = "Late" | "Early" | "On Time" — use this for checked-in staff
  const s = att.status;
  if (s === "Late")      return { label: "Late",         cls: "sdt-badge--orange" };
  if (s === "Early")     return { label: "Early",        cls: "sdt-badge--blue"   };
  if (s === "On Time")   return { label: "On Time",      cls: "sdt-badge--green"  };
  if (att.checkin_status === "Checked-In") return { label: "Checked In", cls: "sdt-badge--green" };
  return { label: "Not Checked In", cls: "sdt-badge--muted" };
};

/**
 * Center modal showing all staff for a specific gym+date.
 *
 * Props:
 *   gymName: string
 *   date: string  — "YYYY-MM-DD"
 *   staffList: array  — all staff records for this gym on this date
 *   onClose: () => void
 */
const MatrixDrillDown = ({ gymName, date, staffList, onClose }) => {
  const [shiftFilter, setShiftFilter] = useState("all");

  const dateLabel = moment(date).format("ddd D MMM YYYY");

  // Flatten: one row per shift_details entry per staff member
  const rows = staffList.flatMap((staff) =>
    (staff.shift_details || []).map((shift) => {
      const att = shift.attendance_details;
      const sv = att?.status_value;
      const isLate = att?.status === "Late" || sv === "late";
      return {
        key: `${staff.user_id}-${shift.start_time}-${shift.end_time}`,
        name: staff.full_name || staff.name || "—",
        role: staff.account_type === "trainer" ? "Trainer" : "Front Desk",
        startTime: shift.start_time,
        endTime: shift.end_time,
        shiftLabel: `${to12h(shift.start_time)} – ${to12h(shift.end_time)}`,
        shiftKey: getShiftKey(shift.start_time),
        checkinTime: att?.checkin_time ? to12h(att.checkin_time) : "—",
        badge: getStatusBadge(att),
        lateBy: isLate && att?.shift_difference ? att.shift_difference : null,
      };
    })
  );

  const filtered =
    shiftFilter === "all"
      ? rows
      : rows.filter((r) => r.shiftKey === shiftFilter);

  const presentCount = staffList.filter((s) =>
    s.shift_details?.some((sh) => {
      const att = sh.attendance_details;
      return att?.checkin_status === "Checked-In" || att?.checkout_status === "checked-out";
    })
  ).length;

  return (
    <>
      <div className="sdm-modal-overlay" onClick={onClose} aria-hidden="true" />
      <div
        className="sdm-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Staff details for ${gymName}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sdm-modal__header">
          <div>
            <div className="sdm-modal__title">{gymName}</div>
            <div className="sdm-modal__subtitle">{dateLabel}</div>
          </div>
          <button
            type="button"
            className="sdm-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="sdm-modal__shift-toggle">
          {["all", "am", "pm"].map((key) => (
            <button
              key={key}
              type="button"
              className={`sdm-modal__shift-btn${shiftFilter === key ? " sdm-modal__shift-btn--active" : ""}`}
              onClick={() => setShiftFilter(key)}
            >
              {key === "all" ? "All Shifts" : key === "am" ? "6AM–2PM / 8AM–5PM" : "2PM–10PM / 1PM–10PM"}
            </button>
          ))}
        </div>

        <div className="sdm-modal__body">
          <div className="sdm-modal__summary">
            {presentCount} / {staffList.length} staff present
            {shiftFilter !== "all" && " (all shifts)"}
          </div>

          {filtered.length === 0 ? (
            <div className="sdm-empty">No staff data for this shift.</div>
          ) : (
            <table className="sdm-modal__table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Shift</th>
                  <th>Check-In</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr key={row.key}>
                    <td>{row.name}</td>
                    <td>{row.role}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{row.shiftLabel}</td>
                    <td>{row.checkinTime}</td>
                    <td>
                      <span className={`sdt-badge ${row.badge.cls}`}>
                        {row.badge.label}
                      </span>
                      {row.lateBy && (
                        <span className="sdm-late-by">+{row.lateBy}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
};

export default MatrixDrillDown;
